import React from 'react';
import {
    CommandBar,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    getTheme,
    ICommandBarItemProps,
    Icon,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    Stack,
    Text
} from '@fluentui/react';
import useCan, { FOLIO_AUTHOR, FOLIO_GLOBAL_ADMIN } from '../../permissions/ability';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import {
    useDeleteCatalogFolioMutation,
    useGetFolioCatalogQuery
} from '../../store/endpoints/FolioAdminEndpoints';
import { IExtensionFolio } from '../../models/Extension';
import { FOLIO_EDITOR_STRINGS as S } from './folioEditorStrings';
import FolioEditorPanel from './FolioEditorPanel';

// Authoring extension folios: the list, and the entry points into the editor.
//
// The list shows only what this caller may edit -- the server filters by ownership, so an author sees
// their own folios and a global admin sees the catalog. Which of the two they are also decides what a new
// folio's id may be, and whether the audience fields are offered at all.
//
// The editing itself lives in FolioEditorPanel, which offers a form over the document and a raw JSON view
// behind it for anything the form does not model.

const theme = getTheme();

/** A folio owned by exactly one organization belongs to it; anything else is global. */
const ownerOf = (folio: IExtensionFolio): string | undefined => {
    const orgs = (folio.organizations ?? []).filter(Boolean);
    return orgs.length === 1 ? orgs[0] : undefined;
};

const FolioEditorPage: React.FC = () => {
    const organization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const canAuthor = useCan(FOLIO_AUTHOR);
    const isGlobalAdmin = useCan(FOLIO_GLOBAL_ADMIN);

    const catalog = useGetFolioCatalogQuery(undefined, { skip: !canAuthor });
    const [remove] = useDeleteCatalogFolioMutation();

    const [editing, setEditing] = React.useState<IExtensionFolio | undefined>();
    const [creating, setCreating] = React.useState(false);
    const [confirmDelete, setConfirmDelete] = React.useState<IExtensionFolio | undefined>();
    const [deleteError, setDeleteError] = React.useState<string | undefined>();

    // Matches the server's rule for a new author-owned id. Global admins are exempt, so their new folios
    // start from an empty id rather than a prefix they would only have to delete.
    const newIdPrefix = isGlobalAdmin ? '' : `${organization ?? ''}-`;

    /**
     * Forking a folio for development: everything the source says, under an id nobody has installed.
     *
     * Opened as a NEW folio rather than saved straight away, so the id and name are the author's to settle
     * before anything is written, and the create guard on save refuses the suggestion if it is taken. The
     * suggestion itself is only a starting point: -dev is what a fork is usually for, and an author's copy
     * still has to carry their organization's prefix, which their source folio already does.
     */
    const copyOf = (folio: IExtensionFolio): IExtensionFolio => {
        const base = folio.id ?? '';
        const suggested = base.endsWith('-dev') ? `${base}-copy` : `${base}-dev`;
        return {
            ...folio,
            id: suggested.startsWith(newIdPrefix) ? suggested : `${newIdPrefix}${suggested}`,
            name: S.copyName(folio.name || base),
            // Never carried over: the copy is a separate connector, and inheriting the source's reach would
            // publish an untested fork to everyone the original is offered to. A global admin can widen it
            // again from the Audience section once it is worth having.
            organizations: isGlobalAdmin ? folio.organizations : undefined,
            entitlements: undefined,
            productLevel: undefined,
        };
    };

    if (!canAuthor) {
        return (
            <div className='main-content'>
                <h1>{S.pageTitle}</h1>
                <MessageBar messageBarType={MessageBarType.warning}>{S.notEntitled}</MessageBar>
            </div>
        );
    }

    const doDelete = async () => {
        if (!confirmDelete?.id) return;
        setDeleteError(undefined);
        const result = await remove(confirmDelete.id);
        if ('error' in result) {
            // Most often the 409: organizations still have it installed, and the message says how many.
            const data = (result.error as { data?: { message?: string } })?.data;
            setDeleteError(data?.message ?? S.deleteFailed);
            return;
        }
        setConfirmDelete(undefined);
    };

    const commands: ICommandBarItemProps[] = [{
        key: 'new',
        text: S.newFolio,
        iconProps: { iconName: 'Add' },
        onClick: () => { setCreating(true); },
    }];

    const farCommands: ICommandBarItemProps[] = [{
        key: 'refresh',
        text: 'Refresh',
        iconOnly: true,
        iconProps: { iconName: 'Refresh' },
        onClick: () => { catalog.refetch(); },
    }];

    const folios = catalog.data ?? [];

    return (
        <div className='main-content'>
            <h1>{S.pageTitle}</h1>
            <Text block style={{ color: theme.palette.neutralSecondary, marginBottom: '0.5em' }}>
                {isGlobalAdmin ? S.introGlobal : S.introOwned}
            </Text>

            <CommandBar items={commands} farItems={farCommands} />

            {catalog.isError && (
                <MessageBar messageBarType={MessageBarType.error}>{S.loadFailed}</MessageBar>
            )}
            {catalog.isLoading && <Spinner label={S.loading} />}
            {!catalog.isLoading && !catalog.isError && folios.length === 0 && (
                <Text>{S.empty}</Text>
            )}

            <Stack tokens={{ childrenGap: '0.5em' }} style={{ marginTop: '1em' }}>
                {folios.map(folio => {
                    const owner = ownerOf(folio);
                    const subCount = Object.keys(folio.subExtensions ?? {}).length;
                    return (
                        <div
                            key={folio.id}
                            style={{
                                border: `1px solid ${theme.palette.neutralLight}`,
                                borderRadius: 4,
                                padding: '0.75em 1em',
                            }}
                        >
                            <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                                <Stack horizontal verticalAlign='center' tokens={{ childrenGap: '0.75em' }}>
                                    {folio.image
                                        ? <img src={folio.image} alt='' style={{ height: 24, maxWidth: 80, objectFit: 'contain' }} />
                                        : <Icon iconName='Trackers' style={{ fontSize: 18 }} />}
                                    <Stack>
                                        <Text style={{ fontWeight: 600 }}>{folio.name || folio.id}</Text>
                                        <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>
                                            {S.folioSummary(folio.id ?? '', subCount, owner)}
                                        </Text>
                                    </Stack>
                                </Stack>
                                <Stack horizontal tokens={{ childrenGap: '0.5em' }}>
                                    <DefaultButton text={S.edit} onClick={() => setEditing(folio)} />
                                    <DefaultButton
                                        text={S.copy}
                                        title={S.copyTooltip}
                                        onClick={() => { setEditing(copyOf(folio)); setCreating(true); }}
                                    />
                                    <DefaultButton
                                        text={S.delete}
                                        onClick={() => { setDeleteError(undefined); setConfirmDelete(folio); }}
                                    />
                                </Stack>
                            </Stack>
                        </div>
                    );
                })}
            </Stack>

            {(editing || creating) && (
                <FolioEditorPanel
                    // A copy passes both: the document to start from, and the fact that it is not saved yet.
                    key={creating ? `new-${editing?.id ?? ''}` : editing?.id}
                    folio={editing}
                    isNew={creating}
                    newIdPrefix={newIdPrefix}
                    isGlobalAdmin={isGlobalAdmin}
                    onClose={() => { setEditing(undefined); setCreating(false); }}
                />
            )}

            <Dialog
                hidden={!confirmDelete}
                onDismiss={() => setConfirmDelete(undefined)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: S.deleteTitle,
                    subText: S.deleteConfirm(confirmDelete?.name || confirmDelete?.id || ''),
                }}
            >
                {deleteError && <MessageBar messageBarType={MessageBarType.error}>{deleteError}</MessageBar>}
                <DialogFooter>
                    <PrimaryButton text={S.delete} onClick={doDelete} />
                    <DefaultButton text={S.cancel} onClick={() => setConfirmDelete(undefined)} />
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default FolioEditorPage;
